// ===========================================
// Booking Hooks
// ===========================================
// React Query hooks for makeup bookings and extra lesson requests

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  makeupBookingApi,
  extraLessonApi,
  BookMakeupSlotInput,
  CreateMakeupSlotInput,
  ExtraLessonRequestStatus,
  CreateExtraLessonRequestInput,
  ScheduleExtraLessonInput,
  RescheduleExtraLessonInput,
  ChangeBookingSlotRoomInput,
  CreateDirectBookingInput,
  MakeupCreditLog,
} from '../api/booking.api';
import { calendarKeys } from './useHybridBooking';

// ===========================================
// QUERY KEYS
// ===========================================

export const bookingKeys = {
  all: ['booking'] as const,
  makeup: {
    all: ['booking', 'makeup'] as const,
    eligibilities: () => [...bookingKeys.makeup.all, 'eligibilities'] as const,
    credits: () => [...bookingKeys.makeup.all, 'credits'] as const,
    slots: (instrumentId: string, termId?: string) =>
      [...bookingKeys.makeup.all, 'slots', instrumentId, termId] as const,
    history: () => [...bookingKeys.makeup.all, 'history'] as const,
    admin: {
      credits: (termId?: string) => [...bookingKeys.makeup.all, 'admin', 'credits', termId] as const,
      studentCredits: (studentId: string, termId: string) =>
        [...bookingKeys.makeup.all, 'admin', 'credits', studentId, termId] as const,
      studentCreditLogs: (studentId: string) =>
        [...bookingKeys.makeup.all, 'admin', 'credit-logs', studentId] as const,
      slots: (params?: { termId?: string; teacherId?: string; includeBooked?: boolean }) =>
        [...bookingKeys.makeup.all, 'admin', 'slots', params] as const,
      eligibilities: (params?: { studentId?: string; unredeemedOnly?: boolean }) =>
        [...bookingKeys.makeup.all, 'admin', 'eligibilities', params] as const,
    },
  },
  extra: {
    all: ['booking', 'extra'] as const,
    requests: () => [...bookingKeys.extra.all, 'requests'] as const,
    settings: () => [...bookingKeys.extra.all, 'settings'] as const,
    teacherAvailabilityDays: (teacherId: string) =>
      [...bookingKeys.extra.all, 'teacher-availability-days', teacherId] as const,
    availableSlots: (teacherId: string, date: string, duration: number) =>
      [...bookingKeys.extra.all, 'available-slots', teacherId, date, duration] as const,
    admin: {
      requests: (params?: { status?: ExtraLessonRequestStatus; studentId?: string }) =>
        [...bookingKeys.extra.all, 'admin', 'requests', params] as const,
      pendingCount: () => [...bookingKeys.extra.all, 'admin', 'pending-count'] as const,
      request: (requestId: string) =>
        [...bookingKeys.extra.all, 'admin', 'request', requestId] as const,
    },
  },
};

// ===========================================
// MAKEUP BOOKING HOOKS - PARENT
// ===========================================

export function useMakeupInstruments() {
  return useQuery({
    queryKey: [...bookingKeys.makeup.all, 'instruments'] as const,
    queryFn: () => makeupBookingApi.getInstruments(),
  });
}

export function useMakeupEligibilities() {
  return useQuery({
    queryKey: bookingKeys.makeup.eligibilities(),
    queryFn: () => makeupBookingApi.getEligibilities(),
  });
}

export function useMakeupCredits() {
  return useQuery({
    queryKey: bookingKeys.makeup.credits(),
    queryFn: () => makeupBookingApi.getCredits(),
  });
}

export function useMakeupSlots(
  instrumentId: string,
  termId?: string,
  teacherId?: string,
  eligibilityId?: string
) {
  return useQuery({
    queryKey: [...bookingKeys.makeup.slots(instrumentId, termId), teacherId, eligibilityId] as const,
    queryFn: () => makeupBookingApi.getAvailableSlots(instrumentId, termId, teacherId, eligibilityId),
    enabled: !!instrumentId,
  });
}

export function useMakeupHistory() {
  return useQuery({
    queryKey: bookingKeys.makeup.history(),
    queryFn: () => makeupBookingApi.getHistory(),
  });
}

export function useBookMakeupSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BookMakeupSlotInput) => makeupBookingApi.bookSlot(input),
    onSuccess: () => {
      // Invalidate all makeup-related queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
    },
    onError: (error: Error & { status?: number }) => {
      // On 409 Conflict (slot was taken by someone else), refresh available slots
      if (error.status === 409) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
      }
    },
  });
}

export function useCancelMakeupBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (slotId: string) => makeupBookingApi.cancelBooking(slotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
    },
  });
}

// ===========================================
// MAKEUP BOOKING HOOKS - ADMIN
// ===========================================

export function useAdminMakeupCredits(termId?: string) {
  return useQuery({
    queryKey: bookingKeys.makeup.admin.credits(termId),
    queryFn: () => makeupBookingApi.adminGetCredits(termId),
  });
}

export function useAdminStudentCredits(studentId: string, termId: string) {
  return useQuery({
    queryKey: bookingKeys.makeup.admin.studentCredits(studentId, termId),
    queryFn: () => makeupBookingApi.adminGetStudentCredits(studentId, termId),
    enabled: !!studentId && !!termId,
  });
}

export function useAdminGrantCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      studentId,
      termId,
      additionalCredits,
      reason,
      offerToFamily,
    }: {
      studentId: string;
      termId: string;
      additionalCredits?: number;
      reason?: string;
      /** true = Send make-up credit (a Book button for the family); false = Add more (office only). */
      offerToFamily?: boolean;
    }) => makeupBookingApi.adminGrantCredit(studentId, termId, additionalCredits, reason, offerToFamily),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
    },
  });
}

export function useAdminStudentCreditLogs(studentId: string) {
  return useQuery({
    queryKey: bookingKeys.makeup.admin.studentCreditLogs(studentId),
    queryFn: (): Promise<MakeupCreditLog[]> => makeupBookingApi.adminGetStudentCreditLogs(studentId),
    enabled: !!studentId,
  });
}

export function useAdminAdjustCredits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      creditId,
      creditsGranted,
      creditsUsed,
    }: {
      creditId: string;
      creditsGranted: number;
      creditsUsed: number;
    }) => makeupBookingApi.adminAdjustCredits(creditId, creditsGranted, creditsUsed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
    },
  });
}

export function useAdminMakeupSlots(params?: {
  termId?: string;
  teacherId?: string;
  includeBooked?: boolean;
}) {
  return useQuery({
    queryKey: bookingKeys.makeup.admin.slots(params),
    queryFn: () => makeupBookingApi.adminGetSlots(params),
  });
}

export function useAdminCreateMakeupSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMakeupSlotInput) => makeupBookingApi.adminCreateSlot(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
    },
  });
}

export function useAdminDeleteMakeupSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (slotId: string) => makeupBookingApi.adminDeleteSlot(slotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
    },
  });
}

export function useAdminRescheduleMakeupSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slotId,
      input,
    }: {
      slotId: string;
      input: { date: string; startTime: string; endTime: string; roomId?: string | null };
    }) => makeupBookingApi.adminRescheduleSlot(slotId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    onError: (error: Error & { status?: number }) => {
      // On 409 (teacher conflict), refresh slot data
      if (error.status === 409) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
      }
    },
  });
}

export function useAdminChangeMakeupSlotRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slotId,
      input,
    }: {
      slotId: string;
      input: ChangeBookingSlotRoomInput;
    }) => makeupBookingApi.adminChangeSlotRoom(slotId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 409) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.makeup.all });
      }
    },
  });
}

export function useAdminMakeupEligibilities(params?: {
  studentId?: string;
  unredeemedOnly?: boolean;
}) {
  return useQuery({
    queryKey: bookingKeys.makeup.admin.eligibilities(params),
    queryFn: () => makeupBookingApi.adminGetEligibilities(params),
  });
}

// ===========================================
// EXTRA LESSON REQUEST HOOKS - PARENT
// ===========================================

export function useExtraLessonRequests() {
  return useQuery({
    queryKey: bookingKeys.extra.requests(),
    queryFn: () => extraLessonApi.getRequests(),
  });
}

export function useCreateExtraLessonRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateExtraLessonRequestInput) => extraLessonApi.createRequest(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
    },
  });
}

export function useCancelExtraLessonRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) => extraLessonApi.cancelRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
    },
  });
}

export function useExtraLessonSettings() {
  return useQuery({
    queryKey: bookingKeys.extra.settings(),
    queryFn: () => extraLessonApi.getSettings(),
  });
}

export function useTeacherAvailabilityDays(teacherId: string | null) {
  return useQuery({
    queryKey: bookingKeys.extra.teacherAvailabilityDays(teacherId || ''),
    queryFn: () => extraLessonApi.getTeacherAvailabilityDays(teacherId!),
    enabled: !!teacherId,
  });
}

export function useAvailableTeacherSlots(
  teacherId: string | null,
  date: Date | null,
  duration: number
) {
  const dateStr = date ? date.toISOString() : '';

  return useQuery({
    queryKey: bookingKeys.extra.availableSlots(teacherId || '', dateStr, duration),
    queryFn: () => extraLessonApi.getAvailableSlots(teacherId!, date!, duration),
    enabled: !!teacherId && !!date && duration > 0,
  });
}

export function useCreateDirectBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateDirectBookingInput) => extraLessonApi.createDirectBooking(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
    },
  });
}

// ===========================================
// EXTRA LESSON REQUEST HOOKS - ADMIN
// ===========================================

export function useAdminExtraLessonRequests(params?: {
  status?: ExtraLessonRequestStatus;
  studentId?: string;
}) {
  return useQuery({
    queryKey: bookingKeys.extra.admin.requests(params),
    queryFn: () => extraLessonApi.adminGetRequests(params),
  });
}

export function useAdminExtraLessonPendingCount() {
  return useQuery({
    queryKey: bookingKeys.extra.admin.pendingCount(),
    queryFn: () => extraLessonApi.adminGetPendingCount(),
  });
}

export function useAdminExtraLessonRequest(requestId: string) {
  return useQuery({
    queryKey: bookingKeys.extra.admin.request(requestId),
    queryFn: () => extraLessonApi.adminGetRequest(requestId),
    enabled: !!requestId,
  });
}

export function useAdminApproveExtraLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, adminNotes }: { requestId: string; adminNotes?: string }) =>
      extraLessonApi.adminApprove(requestId, adminNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
    },
  });
}

export function useAdminRejectExtraLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, adminNotes }: { requestId: string; adminNotes: string }) =>
      extraLessonApi.adminReject(requestId, adminNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
    },
  });
}

export function useAdminScheduleExtraLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestId,
      input,
    }: {
      requestId: string;
      input: ScheduleExtraLessonInput;
    }) => extraLessonApi.adminSchedule(requestId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useAdminRescheduleExtraLesson() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      requestId,
      input,
    }: {
      requestId: string;
      input: RescheduleExtraLessonInput;
    }) => extraLessonApi.adminReschedule(requestId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    // ⚠️ Say WHY. The Calendar's reschedule dialog renders no mutation error, and
    // since holiday make-ups the server refuses a make-up moved past its credit's
    // last day ("Term 3 make-up credits can be used until …") — silence here left
    // the admin with a button that simply re-enabled (/qa whole-feature M1).
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      enqueueSnackbar(error.response?.data?.message || 'Failed to reschedule', { variant: 'error' });
    },
  });
}

export function useAdminChangeExtraLessonRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestId,
      input,
    }: {
      requestId: string;
      input: ChangeBookingSlotRoomInput;
    }) => extraLessonApi.adminChangeRoom(requestId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 409) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
      }
    },
  });
}

export function useAdminCompleteExtraLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) => extraLessonApi.adminComplete(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
    },
  });
}

export function useAdminLinkInvoiceToExtraLesson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, invoiceId }: { requestId: string; invoiceId: string }) =>
      extraLessonApi.adminLinkInvoice(requestId, invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.extra.all });
    },
  });
}
